import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  useTheme,
  CircularProgress
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';
import { holdBillService } from '../../services/holdBillService';

const ViewHoldBillsModal = ({ open, onClose, onProcessBill }) => {
  const theme = useTheme();
  const [holdBills, setHoldBills] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(null); // Track which bill is being processed
  const [error, setError] = useState('');

  const loadHoldBills = async (query = '') => {
    setLoading(true);
    try {
      const data = query
        ? await holdBillService.searchHoldBills(query)
        : await holdBillService.getAllHoldBills();
      setHoldBills(data);
    } catch (error) {
      setError('Failed to load hold bills');
      console.error('Error loading hold bills:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadHoldBills();
    }
  }, [open]);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    loadHoldBills(query);
  };

  const handleDelete = async (id) => {
    try {
      await holdBillService.deleteHoldBill(id);
      setHoldBills(holdBills.filter(bill => bill.id !== id));
    } catch (error) {
      setError('Failed to delete hold bill');
      console.error('Error deleting hold bill:', error);
    }
  };

  const handleProcess = async (bill) => {
    try {
      setError(''); // Clear previous errors
      setProcessing(bill.id); // Set processing state
      console.log('Loading hold bill into cart:', bill.id);
      
      // Get the hold bill data
      const holdBillData = await holdBillService.getHoldBillById(bill.id);
      
      // Pass the hold bill data to the parent component to load into cart
      onProcessBill(holdBillData);
      
      // Close the modal
      onClose();
    } catch (error) {
      console.error('Error loading hold bill:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load hold bill';
      setError(errorMessage);
    } finally {
      setProcessing(null); // Clear processing state
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Hold Bills</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Search Bills"
            value={searchQuery}
            onChange={handleSearch}
            sx={{ mb: 2 }}
          />
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {holdBills.map((bill) => (
                <React.Fragment key={bill.id}>
                  <ListItem>
                    <ListItemText
                      primary={bill.billName}
                      secondary={
                        <>
                          <Typography component="span" variant="body2">
                            Customer: {bill.customerName}
                          </Typography>
                          <br />
                          <Typography component="span" variant="body2">
                            Items: {bill.items.length}
                          </Typography>
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleProcess(bill)}
                        disabled={processing === bill.id}
                        sx={{ mr: 1 }}
                      >
                        {processing === bill.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <ShoppingCartIcon />
                        )}
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleDelete(bill.id)}
                        disabled={processing === bill.id}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              {holdBills.length === 0 && (
                <Typography sx={{ p: 2, textAlign: 'center' }}>
                  No hold bills found
                </Typography>
              )}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewHoldBillsModal; 